import { renderMarkdownAST } from "../../../utils/markdownRenderer.js";
import ImageFallback from "../../../components/imageFallback.js";
import MessageImageAttachment from "./MessageImageAttachment.js";
import ChannelMention from "../../../components/ChannelMention.js";
import MemberMention from "../../../components/MemberMention.js";
import RoleMention from "../../../components/RoleMention.js";
import type {
    MessageComponent,
    MessageComponentMedia,
} from "./messageParser.js";
import {
    DiscordButtonStyle,
    DiscordDefaultAvatarUrl,
    DiscordMessageComponentType,
    DiscordSeparatorSpacing,
} from "./messageComponentConstants.js";

type SelectDefaultValue = {
    id?: unknown;
    type?: unknown;
    label?: unknown;
    name?: unknown;
};

type SelectDefaultValueKind = "user" | "role" | "channel" | "mentionable";

function readString(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function readBoolean(value: unknown): boolean {
    return value === true;
}

function readSelectValueType(value: unknown): "user" | "role" | "channel" | "mentionable" | undefined {
    if (value === "user" || value === "role" || value === "channel" || value === "mentionable")
        return value;

    return undefined;
}

function renderSelectPlaceholder(component: MessageComponent) {
    return (
        <div class="log-message-component-select-placeholder">
            {readString(component.placeholder) || "Choisissez une option"}
        </div>
    );
}

function renderSelectValue(value: SelectDefaultValue, kind: SelectDefaultValueKind) {
    const id = readString(value.id);

    if (!id)
        return null;

    const label = readString(value.label) || readString(value.name) || null;
    const resolvedType = readSelectValueType(value.type) || (kind !== "mentionable" ? kind : undefined);

    if (resolvedType === "user")
        return <MemberMention id={id} fallbackLabel={label} />;

    if (resolvedType === "role")
        return <RoleMention id={id} fallbackLabel={label} />;

    if (resolvedType === "channel")
        return <ChannelMention id={id} fallbackLabel={label} />;

    return (
        <span class="log-message-component-select-fallback-value">
            {label ? `@${label}` : `@${id}`}
        </span>
    );
}

function renderSelectValues(component: MessageComponent, kind: SelectDefaultValueKind) {
    const values = Array.isArray(component.default_values)
        ? component.default_values as SelectDefaultValue[]
        : [];

    const isMultiple = (readNumber(component.max_values) || 1) > 1;

    return (
        <div class={{
            "log-message-component-select"         : true,
            "log-message-component-select-multiple" : isMultiple,
        }}>
            {renderSelectPlaceholder(component)}

            {values.length > 0 ? (
                <div class="log-message-component-select-values">
                    {values.map((value, index) => (
                        <span class="log-message-component-select-value" key={index}>
                            {renderSelectValue(value, kind)}
                        </span>
                    ))}
                </div>
            ) : (
                <span class="log-message-component-select-empty">Aucun élément sélectionné</span>
            )}
        </div>
    );
}

function readMediaUrl(media: MessageComponentMedia | undefined): string | undefined {
    return readString(media?.proxy_url)
        || readString(media?.proxyURL)
        || readString(media?.url);
}

function readColor(value: unknown): string | undefined {
    const color = readNumber(value);

    if (color === undefined)
        return undefined;

    return `#${color.toString(16).padStart(6, "0")}`;
}

function renderMarkdown(value: unknown, fallback?: unknown) {
    if (value)
        return renderMarkdownAST(value);

    return readString(fallback) ?? null;
}

function renderTextDisplay(component: MessageComponent) {
    return (
        <div class="log-message-component-text">
            {renderMarkdown(component.parsedContent, component.content)}
        </div>
    );
}

function renderSeparator(component: MessageComponent) {
    return (
        <div class={{
            "log-message-component-separator"         : true,
            "log-message-component-separator-divider" : component.divider !== false,
            "log-message-component-spacing-large"     : component.spacing === DiscordSeparatorSpacing.Large,
        }} />
    );
}

function renderThumbnail(component: MessageComponent) {
    const url = readMediaUrl(component.media);

    if (!url)
        return null;

    const description = readString(component.description) || "Image";

    return (
        <a class="log-message-component-thumbnail" href={readString(component.media?.url) || url} target="_blank" rel="noreferrer">
            <ImageFallback src={url} default={DiscordDefaultAvatarUrl} alt={description} />
        </a>
    );
}

function renderMediaGallery(component: MessageComponent, onImageSettled?: () => void) {
    const items = component.items?.filter((item) => readMediaUrl(item.media));

    if (!items?.length)
        return null;

    return (
        <div class="log-message-component-gallery">
            {items.map((item) => (
                <MessageImageAttachment
                    src={readMediaUrl(item.media)}
                    href={readString(item.media?.url) || readMediaUrl(item.media)}
                    name={readString(item.description) || "image"}
                    onSettled={onImageSettled}
                />
            ))}
        </div>
    );
}

function renderFile(component: MessageComponent) {
    const url = readMediaUrl(component.file || component.media);

    if (!url)
        return null;

    const name = readString(component.file?.filename)
        || readString(component.file?.name)
        || "fichier";

    return (
        <a class="log-message-component-file" href={url} target="_blank" rel="noreferrer">
            <span class="material-symbols-outlined">draft</span>
            <span>{name}</span>
        </a>
    );
}

function renderButton(component: MessageComponent) {
    const label = readString(component.label) || readString(component.emoji?.name) || "Bouton";
    const url = readString(component.url);
    const className = {
        "log-message-component-button"          : true,
        "log-message-component-button-primary"  : component.style === DiscordButtonStyle.Primary,
        "log-message-component-button-success"  : component.style === DiscordButtonStyle.Success,
        "log-message-component-button-danger"   : component.style === DiscordButtonStyle.Danger,
        "log-message-component-button-link"     : component.style === DiscordButtonStyle.Link,
        "log-message-component-button-disabled" : readBoolean(component.disabled),
    };

    if (url) {
        return (
            <a class={className} href={url} target="_blank" rel="noreferrer">
                {label}
            </a>
        );
    }

    return <span class={className}>{label}</span>;
}

function renderSelect(component: MessageComponent) {
    const options = Array.isArray(component.options)
        ? component.options
        : [];
    const maxValues = readNumber(component.max_values) || 1;
    const isMultiple = maxValues > 1;

    if (!options.length)
        return null;

    const defaultOptions = new Set(
        options
            .filter((option) => readBoolean(option?.default))
            .map((option) => readString(option?.value))
            .filter((value): value is string => Boolean(value)),
    );

    const optionClass = {
        "log-message-component-select"         : true,
        "log-message-component-select-multiple": isMultiple,
    };

    return (
        <div class={optionClass}>
            {renderSelectPlaceholder(component)}
            <select class="log-message-component-select-control" multiple={isMultiple} disabled={readBoolean(component.disabled)} size={isMultiple ? Math.min(options.length, 4) : 1}>
                {options.map((option, index) => (
                    <option
                        value={readString(option?.value) || ""}
                        selected={readString(option?.value) !== undefined && defaultOptions.has(readString(option?.value) as string)}
                    >
                        {readString(option?.label) || readString(option?.value) || `Option ${String(index + 1)}`}
                    </option>
                ))}
            </select>
        </div>
    );
}

function renderUserSelect(component: MessageComponent) {
    return renderSelectValues(component, "user");
}

function renderRoleSelect(component: MessageComponent) {
    return renderSelectValues(component, "role");
}

function renderChannelSelect(component: MessageComponent) {
    return renderSelectValues(component, "channel");
}

function renderMentionableSelect(component: MessageComponent) {
    return renderSelectValues(component, "mentionable");
}

function renderActionRow(component: MessageComponent) {
    if (!component.components?.length)
        return null;

    return (
        <div class="log-message-component-action-row">
            {component.components.map((child) => renderMessageComponent(child))}
        </div>
    );
}

function renderSection(component: MessageComponent, onImageSettled?: () => void) {
    return (
        <div class="log-message-component-section">
            <div class="log-message-component-section-content">
                {component.components?.map((child) => renderMessageComponent(child, onImageSettled))}
            </div>
            {component.accessory && (
                <div class="log-message-component-section-accessory">
                    {renderMessageComponent(component.accessory, onImageSettled)}
                </div>
            )}
        </div>
    );
}

function renderContainer(component: MessageComponent, onImageSettled?: () => void) {
    const accentColor = readColor(component.accent_color) || readColor(component.accentColor);

    return (
        <div
            class="log-message-component-container"
            style={accentColor ? { "--component-accent": accentColor } : undefined}
        >
            {component.components?.map((child) => renderMessageComponent(child, onImageSettled))}
        </div>
    );
}

export function renderMessageComponent(component: MessageComponent, onImageSettled?: () => void) {
    switch (component.type) {
        case DiscordMessageComponentType.ActionRow:
            return renderActionRow(component);
        case DiscordMessageComponentType.Button:
            return renderButton(component);
        case DiscordMessageComponentType.Section:
            return renderSection(component, onImageSettled);
        case DiscordMessageComponentType.TextDisplay:
            return renderTextDisplay(component);
        case DiscordMessageComponentType.Thumbnail:
            return renderThumbnail(component);
        case DiscordMessageComponentType.MediaGallery:
            return renderMediaGallery(component, onImageSettled);
        case DiscordMessageComponentType.File:
            return renderFile(component);
        case DiscordMessageComponentType.Separator:
            return renderSeparator(component);
        case DiscordMessageComponentType.Container:
            return renderContainer(component, onImageSettled);
        case DiscordMessageComponentType.StringSelect:
            return renderSelect(component);
        case DiscordMessageComponentType.UserSelect:
            return renderUserSelect(component);
        case DiscordMessageComponentType.RoleSelect:
            return renderRoleSelect(component);
        case DiscordMessageComponentType.MentionableSelect:
            return renderMentionableSelect(component);
        case DiscordMessageComponentType.ChannelSelect:
            return renderChannelSelect(component);
        default:
            return null;
    }
}

export function renderMessageComponents(components: readonly MessageComponent[] | undefined, onImageSettled?: () => void) {
    if (!components?.length)
        return null;

    return (
        <div class="log-message-components">
            {components.map((component) => renderMessageComponent(component, onImageSettled))}
        </div>
    );
}
